import React from "react";

import type { LoadingStage } from "../../hooks/useDicomSeriesLoader";
import type { StudyMetadataResponse } from "../../utils/dicomMetadataLoader";

interface LoadingOverlayProps {
  stage: LoadingStage;
  metadata?: StudyMetadataResponse | null;
}

// Calm viewport placeholder. No reticle, no rotating fake messages, no spinner —
// just the real stage + what's known (series / image counts, planes) plus the
// single C2 indeterminate sweep. (loading redesign — replaces the prior theater.)
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ stage, metadata }) => {
  const seriesCount = metadata?.series ? Object.keys(metadata.series).length : 0;
  const totalInstances = metadata?.series
    ? Object.values(metadata.series).reduce((sum, arr) => sum + arr.length, 0)
    : 0;
  const planes = metadata?.series_metadata
    ? (Array.from(
        new Set(
          Object.values(metadata.series_metadata)
            .map((m) => m.plane)
            .filter(Boolean),
        ),
      ) as string[])
    : [];

  const label =
    stage === "fetching-metadata"
      ? "Loading study"
      : stage === "initializing-cornerstone"
        ? "Preparing viewer"
        : "Loading images";

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-2">
      <p className="text-sm text-fg-muted">{label}</p>

      {seriesCount > 0 && (
        <p className="font-mono text-[10px] tabular-nums tracking-[0.04em] text-fg-dim">
          {seriesCount} series · {totalInstances.toLocaleString()} images
        </p>
      )}

      {planes.length > 0 && (
        <div className="mt-0.5 flex items-center gap-1.5">
          {planes.map((plane) => (
            <span
              key={plane}
              className="rounded-xs bg-surface px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-fg-dim"
            >
              {plane}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 h-0.5 w-28 overflow-hidden">
        <div className="fetch-sweep h-full w-full" />
      </div>
    </div>
  );
};
